import React, { MouseEvent, useMemo } from "react";
import styles from "./sectionModule.module.scss";
import { Content, ContentType, Lesson } from "src/types";
import IconPDF from "src/assets/icons/content_type_pdf.svg?react";
import IconAudio from "src/assets/icons/content_type_audio.svg?react";
import IconSlide from "src/assets/icons/content_type_slide.svg?react";
import IconRevealSlide from "@assets/icons/content_type_slide_com.svg?react";
import IconVideo from "src/assets/icons/content_type_video.svg?react";
import IconFeedback from "src/assets/icons/content_type_feedback.svg?react";
import IconMenu from "src/assets/icons/menu.svg?react";
import { useEditUnit } from "../../../edit/useEditUnit";
import { DraggableProvided, DraggableStateSnapshot } from "react-beautiful-dnd";
import { Section } from "@src/api/graphql-codegen";

type Props = {
  content: Content;
  lesson?: Lesson;
  selected?: boolean;
  draggableProvided?: DraggableProvided;
  snapshot?: DraggableStateSnapshot;
  isPreparation?: boolean;
  section: Section;
};

const getContentIcon = (type: ContentType) => {
  switch (type) {
    case ContentType.PDF: {
      return IconPDF;
    }
    case ContentType.AUDIO: {
      return IconAudio;
    }
    // case ContentType.GOOGLESLIDE:
    case ContentType.SLIDE: {
      return IconSlide;
    }
    case ContentType.REVEALSLIDE: {
      return IconSlide;
    }
    case ContentType.ZIPSLIDE: {
      return IconRevealSlide;
    }
    case ContentType.VIDEO: {
      return IconVideo;
    }
    case ContentType.FORM: {
      return IconFeedback;
    }
    default: {
      return null;
    }
  }
};

function SectionModule(props: Props) {
  const {
    draggableProvided,
    snapshot,
    content,
    selected,
    lesson,
    isPreparation,
    section,
  } = props;
  const { name } = content;

  const { setContent, setLesson, setSelectionLevel, setIsPreparation } =
    useEditUnit();

  const Icon = getContentIcon(content.type);

  const handleSelect = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setLesson(lesson);
    setContent(content);
    setSelectionLevel("SectionContent");
    setIsPreparation(isPreparation);
  };

  const finalIndex =
    section?.modules?.findIndex((module) => module.additionalAfter === true) ??
    -1;
  const currentIndex =
    section?.modules?.findIndex((module) => module.id === content.id) ?? -1;
  const finalContent = finalIndex > -1 && currentIndex === finalIndex;
  const additionalContent =
    finalIndex > -1 &&
    currentIndex > finalIndex &&
    content?.type !== ContentType.FORM &&
    !content?.name.toLowerCase().includes("exit ticket");
  const completeContent = content.lastInLesson === true;
  const wrapperClassName = useMemo(() => {
    const classes = [styles.wrapper];
    if (selected) {
      classes.push(styles.selected);
    }
    if (snapshot?.isDragging) {
      classes.push(styles.moving);
    }
    return classes.join(" ");
  }, [selected, snapshot?.isDragging]);

  return (
    <div
      className={wrapperClassName}
      onClick={handleSelect}
      {...draggableProvided?.dragHandleProps}
    >
      <div
        className={styles.menu}
        style={{
          display: snapshot?.isDragging ? "flex" : "",
        }}
      >
        <IconMenu />
      </div>
      <div className={styles.circle} />
      <div className={styles.title}>
        {name}
        {finalContent ? (
          " (F)"
        ) : additionalContent ? (
          <strong>{completeContent ? " (C)" : " (A)"}</strong>
        ) : (
          <strong>{completeContent ? " (C)" : ""}</strong>
        )}
      </div>
      <div className={styles.meta}></div>
      <div className={styles.typeIconWrapper}>
        {content.type === ContentType.PARENT ? (
          <div className={styles.paColor}>PS</div>
        ) : (
          Icon && <Icon className={styles.typeIcon} />
        )}
      </div>
    </div>
  );
}

export default React.memo(SectionModule);
